/* eslint-disable @typescript-eslint/no-explicit-any -- styled-components Interpolation<P> contravariance requires any; theme shape is dynamic */
import { css } from 'styled-components';

/**
 * Calculate icon dimensions from iconSize prop
 * @param {string|number} iconSize - Size value (sm|md|lg|xl|xxl or numeric)
 * @param {object} theme - Theme context with iconSizes
 * @returns {string|number} Resolved dimension value
 */
export const getIconDimensions = (iconSize: string | number | undefined, theme: Record<string, any>) => {
  if (!iconSize) {
    return theme.iconSizes.md;
  }
  if (iconSize === 'auto') {
    return 'auto';
  }
  const sizeStr = String(iconSize);
  if (sizeStr.match(/^(sm|md|lg|xl|xxl)$/)) {
    return theme.iconSizes?.[iconSize] || theme.iconSizes?.md;
  }
  const numericSize = sizeStr.replace(/\D/g, '');
  return numericSize || theme.iconSizes?.md;
};

/**
 * Generate transform CSS for rotation and flip
 * Use with styled-components: ${getTransformCSS}
 */
export const getTransformCSS = css<{ $degreesOfIconRotation?: number; $flipOnXAxis?: boolean }>`
  transform: ${({ $degreesOfIconRotation, $flipOnXAxis }) => {
    const rotation = $degreesOfIconRotation ? `rotate(${$degreesOfIconRotation}deg)` : '';
    const flip = $flipOnXAxis ? 'scaleX(-1)' : '';
    return rotation || flip ? `${rotation} ${flip}`.trim() : 'none';
  }};
`;

/**
 * Generate common CSS for fill color and shadow
 * Use with styled-components: ${getCommonCSS}
 */
export const getCommonCSS = css<{ $fillColor?: string; $shadowed?: boolean }>`
  ${({ $fillColor }) => ($fillColor ? `fill: ${$fillColor}; color: ${$fillColor};` : '')}
  ${({ $shadowed }) => ($shadowed ? 'filter: drop-shadow(2px 2px 2px rgba(0, 0, 0, 0.15));' : '')}
`;

/**
 * Normalize dimension value to CSS-compatible string
 * @param {string|number} value - Dimension value
 * @returns {string} CSS dimension string
 */
export const normalizeDimension = (value: string | number | undefined): string => {
  if (!value) {
    return 'auto';
  }
  if (typeof value === 'number') {
    return `${value}px`;
  }
  // Check for numeric string (e.g. "24") and append px
  if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
    return `${value}px`;
  }
  return value;
};

/**
 * Set fill attribute to currentColor on an SVG element
 * @param {Element} el - SVG element
 */
export const setFillToCurrentColor = (el: Element | null | undefined) => el?.setAttribute?.('fill', 'currentColor');

/**
 * Remove fill attribute from element and all children
 * @param {Element} svg - SVG element
 */
export const removeFillFromSvg = (svg: Element | null | undefined) => {
  svg?.removeAttribute?.('fill');
  [...(svg?.querySelectorAll?.('*') || [])].forEach(child => child.removeAttribute('fill'));
};

/**
 * Apply coloring logic to SVG based on icon type
 * @param {Element} svg - SVG element
 * @param {boolean} isIconColored - Whether icon is a .color icon
 * @param {boolean} removeFill - Whether to remove fills
 */
export const applyIconColoring = (
  svg: Element | null | undefined,
  isIconColored: boolean | undefined,
  removeFill: boolean | undefined,
) => {
  if (removeFill) {
    removeFillFromSvg(svg);
  } else if (!isIconColored) {
    setFillToCurrentColor(svg);
  }
};

/**
 * Apply accessibility attributes to SVG
 * @param {Element} svg - SVG element
 * @param {string} title - Accessible title/label
 */
export const applyA11yAttributes = (svg: Element, title: string | undefined) => {
  if (title) {
    svg.setAttribute('aria-label', title);
    svg.removeAttribute('aria-hidden');
  } else {
    svg.setAttribute('aria-hidden', 'true');
  }
};

/**
 * Build accessibility attributes for icons
 * @param {object} params
 * @param {string} params.title - Accessible title
 * @param {string} params.ariaLabel - Accessible label
 * @param {string} params.role - Role override
 * @returns {{ role: string, ariaLabel: (string|undefined), ariaHidden: (string|undefined), title: (string|undefined) }}
 */
export const getIconA11y = ({ title, ariaLabel, role }: { title?: string; ariaLabel?: string; role?: string }) => {
  const a11yTitle = title || ariaLabel || undefined;
  const finalRole = role || (a11yTitle ? 'img' : 'presentation');
  return {
    role: finalRole,
    'aria-label': a11yTitle,
    title: a11yTitle,
  };
};

/**
 * Calculate final container dimensions based on props and theme
 * @param {object} params
 * @param {string|number} params.width - Explicit width prop
 * @param {string|number} params.height - Explicit height prop
 * @param {string|number} params.size - Size prop
 * @param {string|number} params.iconSize - Icon size category
 * @param {object} params.theme - Theme object
 * @returns {object} { width, height }
 */
export const calculateContainerDimensions = ({
  width,
  height,
  size,
  iconSize,
  theme,
}: {
  width?: string | number;
  height?: string | number;
  size?: string | number;
  iconSize?: string | number;
  theme: Record<string, any>;
}) => {
  const baseSize = getIconDimensions(iconSize, theme);
  const resolvedSize = size && theme.iconSizes?.[size] ? theme.iconSizes[size] : size;

  return {
    width: width || resolvedSize || `${baseSize}px`,
    height: height || resolvedSize || `${baseSize}px`,
  };
};

/**
 * Configure SVG element attributes before injection
 * @param {Element} svg - SVG element
 * @param {object} config
 * @param {string} config.width - CSS width
 * @param {string} config.height - CSS height
 * @param {string} config.viewBox - Custom viewBox
 * @param {string} config.title - A11y title
 * @param {boolean} config.isIconColored - Is colored icon
 * @param {boolean} config.removeFill - Remove fill flag
 */
export const configureSvg = (
  svg: Element,
  {
    width,
    height,
    viewBox,
    title,
    isIconColored,
    removeFill,
    role,
  }: {
    width?: string;
    height?: string;
    viewBox?: string;
    title?: string;
    isIconColored?: boolean;
    removeFill?: boolean;
    role?: string;
  },
) => {
  svg.setAttribute('style', 'display: block; flex-shrink: 0; backface-visibility: hidden;');
  if (width && width !== 'auto') {
    svg.setAttribute('width', width);
  }
  if (height && height !== 'auto') {
    svg.setAttribute('height', height);
  }
  svg.setAttribute('stroke-width', '0');
  if (viewBox) {
    svg.setAttribute('viewBox', viewBox);
  }
  if (role) {
    svg.setAttribute('role', role);
  }
  applyA11yAttributes(svg, title);
  applyIconColoring(svg, isIconColored, removeFill);
};
